import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  VStack,
  HStack,
  Text,
  Tag,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Flex,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { PaginatedCourse, Course } from './types';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ChevronDown, ChevronLeftIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

export function ReviewSupersededSettings({
  currentCourse,
  replacementCourse,
  goToPrevStep,
  handleSubmit,
  isSubmitting,
  title,
}: {
  currentCourse: PaginatedCourse;
  replacementCourse: Course;
  goToPrevStep: () => void;
  handleSubmit: () => void;
  isSubmitting: boolean;
  title: string;
}) {
  const { data: currentCourseData } = trpcAdmin.courses.byId.useQuery(currentCourse.id);
  const { data: replacementCourseData } = trpcAdmin.courses.byId.useQuery(replacementCourse.id);

  // Manual overrides for unit mappings
  const [manualMappings, setManualMappings] = useState<Record<string, string | null>>({});

  // Get all units from both courses
  const currentUnits = useMemo(() => {
    return (
      currentCourseData?.courseItems
        .filter((item) => item.unitOfCompetency != null)
        .map((item) => ({
          courseItemId: item.id,
          unit: item.unitOfCompetency,
        })) ?? []
    );
  }, [currentCourseData]);

  const supersedingUnits = useMemo(() => {
    return (
      replacementCourseData?.courseItems
        .filter((item) => item.unitOfCompetency != null)
        .map((item) => item.unitOfCompetency) ?? []
    );
  }, [replacementCourseData]);

  // Auto-match units by code
  const unitMappings = useMemo(() => {
    const autoMappings: Record<string, string> = {};
    currentUnits.forEach(({ courseItemId, unit }) => {
      if (!unit) {
        return;
      }

      const match = supersedingUnits.find(
        (superUnit) => !!superUnit && superUnit.code === unit.code,
      );

      if (match) {
        autoMappings[courseItemId] = match.id;
      }
    });
    return autoMappings;
  }, [currentUnits, supersedingUnits]);

  // Merge auto + manual mappings
  const finalMappings = useMemo(
    () => ({ ...unitMappings, ...manualMappings }),
    [unitMappings, manualMappings],
  );

  // Count handled units (either mapped to a unit or marked as no equivalent)
  const handledUnitsCount = currentUnits.filter(
    ({ courseItemId }) => courseItemId in finalMappings,
  ).length;

  // Check if all units have been addressed
  const allUnitsHandled = handledUnitsCount === currentUnits.length && currentUnits.length > 0;
  const unhandledUnitsCount = currentUnits.length - handledUnitsCount;

  return (
    <Box display="flex" flexDirection="column">
      <Text fontSize="lg" fontWeight="semibold" mb={4}>
        {title}
      </Text>
      <VStack spacing={4} align="stretch">
        {allUnitsHandled ? (
          <Alert status="success" data-testid="all-units-mapped-alert">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              <Trans>All units successfully mapped</Trans>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription fontSize="sm">
              {plural(unhandledUnitsCount, {
                one: '# unit unable to be automatically mapped',
                other: '# units unable to be automatically mapped',
              })}
            </AlertDescription>
          </Alert>
        )}

        <VStack
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="md"
          spacing={0}
          align="stretch"
        >
          <Text
            fontWeight="medium"
            fontSize="sm"
            padding={3}
            borderBottomWidth="1px"
            borderColor="border.default"
          >
            <Trans>Unit mappings</Trans>
          </Text>
          <Box
            maxHeight={pxToRem(320)}
            overflowY="auto"
            width="100%"
            data-testid="unit-mappings-list"
          >
            {currentUnits.map(({ courseItemId, unit }) => {
              if (!unit) {
                return;
              }
              const mappedUnitId = finalMappings[courseItemId];
              const mappedUnit = supersedingUnits.find((u) => !!u && u.id === mappedUnitId);
              const hasMapping = courseItemId in finalMappings;
              const isNoEquivalent = hasMapping && mappedUnitId === null;

              return (
                <VStack
                  key={courseItemId}
                  py={3}
                  px={4}
                  borderBottomWidth="1px"
                  borderColor="border.default"
                  width="100%"
                  align="start"
                  spacing={2}
                >
                  <HStack>
                    <Text fontSize="sm" fontWeight="medium">
                      {unit.code}
                    </Text>
                    {Boolean(unit.unitContextCategory) && (
                      <Tag
                        size="sm"
                        fontSize="xs"
                        fontWeight="bold"
                        colorScheme="blackAlpha"
                        variant="solid"
                      >
                        {unit.unitContextCategory?.categoryName}
                      </Tag>
                    )}
                    <Text fontSize="sm" noOfLines={1}>
                      {unit.title}
                    </Text>
                  </HStack>

                  <Menu matchWidth>
                    <MenuButton
                      as={Button}
                      width="100%"
                      textAlign="left"
                      variant="outline"
                      colorScheme="gray"
                      size="sm"
                      color="gray.600"
                      fontWeight="normal"
                      rightIcon={<ChevronDown size={iconSize.md} />}
                    >
                      {mappedUnit ? (
                        <VStack align="start" gap={1} width="100%" minWidth={0}>
                          <HStack>
                            <Text width="auto" fontSize="sm" fontWeight="medium">
                              {mappedUnit.code}
                            </Text>
                            {Boolean(mappedUnit.unitContextCategory) && (
                              <Tag
                                size="sm"
                                width="auto"
                                fontSize="xs"
                                fontWeight="bold"
                                colorScheme="blackAlpha"
                                variant="solid"
                              >
                                {mappedUnit.unitContextCategory?.categoryName}
                              </Tag>
                            )}
                            <Text fontSize="sm">{mappedUnit.title}</Text>
                          </HStack>
                        </VStack>
                      ) : isNoEquivalent ? (
                        <Text fontSize="sm" color="gray.500">
                          <Trans>No equivalent unit mapping</Trans>
                        </Text>
                      ) : (
                        <Trans>Select unit mapping</Trans>
                      )}
                    </MenuButton>
                    <MenuList maxHeight={pxToRem(360)} overflowY="scroll">
                      <MenuItem
                        key={0}
                        onClick={() => {
                          setManualMappings((prev) => ({
                            ...prev,
                            [courseItemId]: null,
                          }));
                        }}
                      >
                        <Trans>No equivalent unit mapping</Trans>
                      </MenuItem>
                      {supersedingUnits.map((superUnit) => {
                        if (!superUnit) {
                          return;
                        }
                        return (
                          <MenuItem
                            key={superUnit.id}
                            onClick={() => {
                              setManualMappings((prev) => ({
                                ...prev,
                                [courseItemId]: superUnit.id,
                              }));
                            }}
                          >
                            <VStack align="start" gap={1} width="100%" minWidth={0}>
                              <HStack>
                                <Text width="auto" fontSize="sm" fontWeight="medium">
                                  {superUnit.code}
                                </Text>
                                {Boolean(superUnit.unitContextCategory) && (
                                  <Tag
                                    size="sm"
                                    width="auto"
                                    fontSize="xs"
                                    fontWeight="bold"
                                    colorScheme="blackAlpha"
                                    variant="solid"
                                  >
                                    {superUnit.unitContextCategory?.categoryName}
                                  </Tag>
                                )}
                                <Text fontSize="sm" color="gray.600" isTruncated>
                                  {superUnit.title}
                                </Text>
                              </HStack>
                            </VStack>
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </Menu>
                </VStack>
              );
            })}
          </Box>
        </VStack>
      </VStack>

      <Flex justifyContent="space-between" pt={4}>
        <Button
          variant="solid"
          colorScheme="gray"
          onClick={() => {
            goToPrevStep();
          }}
          leftIcon={<ChevronLeftIcon />}
        >
          <Trans>Back</Trans>
        </Button>
        <Button
          colorScheme="brand"
          onClick={() => {
            handleSubmit();
          }}
          isLoading={isSubmitting}
        >
          <Trans>Save</Trans>
        </Button>
      </Flex>
    </Box>
  );
}
